import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Paper, Typography, Chip, CircularProgress,
  List, ListItem, ListItemText, Divider, Link, Grid, Accordion,
  AccordionSummary, AccordionDetails, Badge, Checkbox, Pagination,
  Collapse, Autocomplete, FormControl, InputLabel, Select, MenuItem,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import { searchMotions } from '../services/api';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import DateHierarchyFilter from './DateHierarchyFilter';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import RSSButton from './RSSButton';
import { sortFacetsBooleansFirst } from '../utils/facetOrder';

export default function NoticesOfMotionSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [expandedMotions, setExpandedMotions] = useState({});
  const [expandedMetadata, setExpandedMetadata] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });

  // Date drill-down state
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const [availableOptions, setAvailableOptions] = useState({
    movers: [],
    chambers: [],
    subtypes: []
  });
  const [advancedForm, setAdvancedForm] = useState({
    keywords: '',
    mover: '',
    chamber: '',
    subtype: '',
    dateFrom: '',
    dateTo: ''
  });

  const examples = [
    'mover:"Senator Lambie"',
    'chamber:"Senate"',
    'motion_subtype:"Business of the Senate"',
    'title:estimates'
  ];

  // Transform APH parliamentarian links to ParlSearch links
  const transformAphLinks = (html) => {
    if (!html) return html;
    // Match APH links like: http://www.aph.gov.au/Senators_and_Members/Parliamentarian?MPID=169119
    // and https://www.aph.gov.au/Senators_and_Members/Parliamentarian?MPID=169119
    return html.replace(
      /href=["']https?:\/\/(?:www\.)?aph\.gov\.au\/Senators_and_Members\/Parliamentarian\?MPID=(\d+)["']\s*target=["']_blank["']/gi,
      'href="/parliamentarians/$1"'
    );
  };

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    const urlFilters = searchParams.get('filters');

    if (urlQuery) {
      const parsedFilters = urlFilters ? JSON.parse(urlFilters) : {};
      setActiveFilters(parsedFilters);
      handleSearch(urlQuery, 1, parsedFilters);
    } else {
      handleSearch('*', 1, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Extract options from facets when results change
  useEffect(() => {
    if (results?.facets) {
      const newOptions = { ...availableOptions };
      if (results.facets.movers) {
        const movers = results.facets.movers.map(f => f.value).filter(Boolean).sort();
        if (movers.length > 0) newOptions.movers = [...new Set([...newOptions.movers, ...movers])].sort();
      }
      if (results.facets.chambers) {
        const chambers = results.facets.chambers.map(f => f.value).filter(Boolean).sort();
        if (chambers.length > 0) newOptions.chambers = [...new Set([...newOptions.chambers, ...chambers])].sort();
      }
      if (results.facets.motion_subtypes) {
        const subtypes = results.facets.motion_subtypes.map(f => f.value).filter(Boolean).sort();
        if (subtypes.length > 0) newOptions.subtypes = [...new Set([...newOptions.subtypes, ...subtypes])].sort();
      }
      setAvailableOptions(newOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:"${v}"`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });

    if (filterParts.length === 0) {
      return String(baseQuery || '*');
    }

    const filterQuery = filterParts.join(' AND ');
    if (baseQuery && baseQuery !== '*') {
      return `(${baseQuery}) AND ${filterQuery}`;
    }
    return filterQuery;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = null, year = selectedYear, month = selectedMonth, pageSize = perPage) => {
    const queryToUse = typeof searchQuery === 'string' ? searchQuery : String(query || '*');
    const filtersToUse = filters !== null && typeof filters === 'object' && !filters.nativeEvent ? filters : activeFilters;

    const fullQuery = buildQueryWithFilters(queryToUse, filtersToUse);

    if (!fullQuery || fullQuery.trim() === '') {
      return;
    }

    const urlParams = { q: queryToUse };
    if (Object.keys(filtersToUse).length > 0) {
      urlParams.filters = JSON.stringify(filtersToUse);
    }
    if (pageSize !== DEFAULT_PAGE_SIZE) urlParams.pageSize = pageSize;
    setSearchParams(urlParams);

    setLoading(true);
    try {
      const data = await searchMotions(fullQuery, newPage, pageSize, year, month);
      setResults(data);
      setPage(newPage);
    } catch (error) {
      console.error('Search error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Search failed. Check console for details.';
      alert(`Search failed: ${errorMsg}`);
    }
    setLoading(false);
  };

  // Date drill-down handlers
  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, year, null);
  };

  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    setSelectedDay(null);
    const year = month.split('-')[0];
    setSelectedYear(year);
    handleSearch(null, 1, activeFilters, year, month);
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
  };

  const clearDateDrillDown = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, null, null);
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(query, newPage, activeFilters);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(query, 1, activeFilters, selectedYear, selectedMonth, newSize);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) {
      newFilters[field] = [];
    }

    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) {
        delete newFilters[field];
      }
    } else {
      newFilters[field].push(value);
    }

    setActiveFilters(newFilters);
    handleSearch(query, 1, newFilters);
  };

  const removeFilter = (field, value) => {
    handleFacetClick(field, value);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(query, 1, {});
  };

  const isFilterActive = (field, value) => {
    return activeFilters[field]?.includes(value) || false;
  };

  const facetToField = (facetName) => {
    const mapping = {
      'chambers': 'chamber',
      'movers': 'movers',
      'motion_types': 'motion_type',
      'motion_subtypes': 'motion_subtype'
    };
    return mapping[facetName] || facetName;
  };

  const facetDisplayName = (facetName) => {
    const names = {
      'chambers': 'Chamber',
      'movers': 'Mover',
      'motion_types': 'Motion Type',
      'motion_subtypes': 'Motion Subtype'
    };
    return names[facetName] || facetName.replace('_', ' ');
  };

  const toggleMotionExpansion = (motionId) => {
    setExpandedMotions(prev => ({
      ...prev,
      [motionId]: !prev[motionId]
    }));
  };

  const toggleMetadataExpansion = (motionId) => {
    setExpandedMetadata(prev => ({
      ...prev,
      [motionId]: !prev[motionId]
    }));
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (advancedForm.keywords?.trim()) {
      parts.push(advancedForm.keywords.trim());
    }
    if (advancedForm.mover?.trim()) {
      parts.push(`movers:"${advancedForm.mover.trim()}"`);
    }
    if (advancedForm.chamber?.trim()) {
      parts.push(`chamber:"${advancedForm.chamber.trim()}"`);
    }
    if (advancedForm.subtype?.trim()) {
      parts.push(`motion_subtype:"${advancedForm.subtype.trim()}"`);
    }
    if (advancedForm.dateFrom || advancedForm.dateTo) {
      const from = advancedForm.dateFrom || '*';
      const to = advancedForm.dateTo || '*';
      parts.push(`date:[${from} TO ${to}]`);
    }

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const handleAdvancedSearch = () => {
    const advQuery = buildAdvancedQuery();
    setQuery(advQuery);
    setActiveFilters({});
    handleSearch(advQuery, 1, {});
  };

  const resetAdvancedForm = () => {
    setAdvancedForm({
      keywords: '',
      mover: '',
      chamber: '',
      subtype: '',
      dateFrom: '',
      dateTo: ''
    });
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Notices of Motion</Typography>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder='Try: mover:"Senator Lambie" or chamber:"Senate"'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={resetAdvancedForm} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Examples:
            </Typography>
            {examples.map((ex, i) => (
              <Chip
                key={i}
                label={ex}
                size="small"
                onClick={() => setQuery(ex)}
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={advancedForm.keywords}
                onChange={(e) => setAdvancedForm({...advancedForm, keywords: e.target.value})}
                placeholder="Search in motion text..."
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.movers}
                value={advancedForm.mover}
                onChange={(e, v) => setAdvancedForm({...advancedForm, mover: v || ''})}
                renderInput={(params) => <TextField {...params} label="Mover" />}
                freeSolo
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Chamber</InputLabel>
                <Select
                  value={advancedForm.chamber}
                  onChange={(e) => setAdvancedForm({...advancedForm, chamber: e.target.value})}
                  label="Chamber"
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="Senate">Senate</MenuItem>
                  <MenuItem value="House of Representatives">House of Representatives</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.subtypes}
                value={advancedForm.subtype}
                onChange={(e, v) => setAdvancedForm({...advancedForm, subtype: v || ''})}
                renderInput={(params) => <TextField {...params} label="Motion Subtype" />}
                freeSolo
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date From"
                type="date"
                value={advancedForm.dateFrom}
                onChange={(e) => setAdvancedForm({...advancedForm, dateFrom: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date To"
                type="date"
                value={advancedForm.dateTo}
                onChange={(e) => setAdvancedForm({...advancedForm, dateTo: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleAdvancedSearch}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={resetAdvancedForm} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {results && (
        <Grid container spacing={3}>
          {/* Facets Panel */}
          {results.facets && Object.keys(results.facets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {(Object.keys(activeFilters).length > 0 || selectedYear) && (
                    <Button size="small" onClick={() => { clearAllFilters(); clearDateDrillDown(); }} sx={{ minWidth: 'auto', fontSize: '0.75rem' }}>
                      Clear All
                    </Button>
                  )}
                </Box>

                {/* Date Hierarchy Filter */}
                {(results.facets?.years?.length > 0 || results.facets?.months?.length > 0 || results.facets?.days?.length > 0) && (
                  <DateHierarchyFilter
                    facets={results.facets}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedDay={selectedDay}
                    onYearClick={handleYearClick}
                    onMonthClick={handleMonthClick}
                    onDayClick={handleDayClick}
                    onClearAll={clearDateDrillDown}
                  />
                )}

                {/* Active Filters */}
                {Object.keys(activeFilters).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Active Filters:
                    </Typography>
                    {Object.entries(activeFilters).map(([field, values]) =>
                      values.map((value) => (
                        <Chip
                          key={`${field}-${value}`}
                          label={value}
                          size="small"
                          onDelete={() => removeFilter(field, value)}
                          sx={{ mr: 0.5, mb: 0.5, fontSize: '0.75rem' }}
                        />
                      ))
                    )}
                  </Box>
                )}

                {sortFacetsBooleansFirst(['chambers', 'movers', 'motion_subtypes'], results.facets).map((facetName) => (
                  results.facets[facetName] && results.facets[facetName].length > 0 && (
                    <Accordion key={facetName} defaultExpanded={facetName === 'chambers' || facetName === 'movers'}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          {facetDisplayName(facetName)}
                          {activeFilters[facetToField(facetName)]?.length > 0 && (
                            <Badge
                              badgeContent={activeFilters[facetToField(facetName)].length}
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0 }}>
                          {results.facets[facetName].slice(0, 15).map((facet) => (
                            <ListItem
                              key={facet.value}
                              button
                              onClick={() => handleFacetClick(facetToField(facetName), facet.value)}
                              sx={{ px: 0, py: 0.5 }}
                            >
                              <Checkbox
                                edge="start"
                                checked={isFilterActive(facetToField(facetName), facet.value)}
                                size="small"
                                sx={{ py: 0 }}
                              />
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {facet.value || '(empty)'}
                                    </Typography>
                                    <Chip
                                      label={facet.count}
                                      size="small"
                                      sx={{ height: 20, fontSize: '0.75rem' }}
                                    />
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))}
              </Paper>
            </Grid>
          )}

          {/* Results Panel */}
          <Grid item xs={12} md={results.facets && Object.keys(results.facets).length > 0 ? 9 : 12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Found {results.total.toLocaleString()} results ({results.took_ms}ms)
                </Typography>
                <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                <RSSButton collection="motions" query={query || '*'} />
              </Box>
              {results.total > perPage && (
                <Pagination
                  count={Math.ceil(results.total / perPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              )}
            </Box>

            <List>
              {results.hits.map((hit, index) => (
                <React.Fragment key={hit.id}>
                  <ListItem alignItems="flex-start" sx={{ px: 0, flexDirection: 'column' }}>
                    <ListItemText
                      sx={{ width: '100%' }}
                      primary={
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Typography
                              variant="resultTitle"
                              component={RouterLink}
                              to={`/motions/${hit.id}`}
                              sx={{
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {hit.title || 'Untitled Motion'}
                            </Typography>
                            {hit.chamber && (
                              <Chip
                                label={hit.chamber}
                                color={hit.chamber === 'Senate' ? 'error' : 'primary'}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                            {[
                              hit.movers?.length > 0 && `Mover: ${hit.movers.join(', ')}`,
                              hit.motion_subtype,
                              hit.date
                            ].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <>
                          {/* Summary */}
                          {hit.summary && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                {hit.summary}
                              </Typography>
                            </Box>
                          )}

                          {/* Motion Content */}
                          {(hit.content || hit.content_html) && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Motion Text:
                              </Typography>
                              {hit.content_html ? (
                                <Typography
                                  className="ps-snippet"
                                  variant="body2"
                                  component="div"
                                  sx={{
                                    color: 'text.primary',
                                    '& ol, & ul': { pl: 2, my: 1 },
                                    '& li': { mb: 0.5 }
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: transformAphLinks(
                                      expandedMotions[hit.id]
                                        ? hit.content_html
                                        : hit.content_html.substring(0, 500) + (hit.content_html.length > 500 ? '...' : '')
                                    )
                                  }}
                                />
                              ) : (
                                <Typography className="ps-snippet" variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                                  {expandedMotions[hit.id] ? hit.content : hit.content?.substring(0, 300)}
                                  {!expandedMotions[hit.id] && hit.content?.length > 300 && '...'}
                                </Typography>
                              )}
                              {(hit.content?.length > 300 || hit.content_html?.length > 500) && (
                                <Button
                                  size="small"
                                  onClick={() => toggleMotionExpansion(hit.id)}
                                  sx={{ textTransform: 'none', fontSize: '0.875rem', p: 0, ml: 0.5 }}
                                >
                                  {expandedMotions[hit.id] ? 'Show less' : 'Show more'}
                                </Button>
                              )}
                            </Box>
                          )}

                          {/* Expanded Metadata */}
                          {expandedMetadata[hit.id] && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 0.5, mb: 1, bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                              {hit.title && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Title:</Typography>
                                  <Typography variant="body2">{hit.title}</Typography>
                                </>
                              )}
                              {hit.chamber && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Chamber:</Typography>
                                  <Typography variant="body2">{hit.chamber}</Typography>
                                </>
                              )}
                              {hit.movers?.length > 0 && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Mover(s):</Typography>
                                  <Typography variant="body2">{hit.movers.join(', ')}</Typography>
                                </>
                              )}
                              {hit.motion_type && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Motion Type:</Typography>
                                  <Typography variant="body2">{hit.motion_type}</Typography>
                                </>
                              )}
                              {hit.motion_subtype && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Subtype:</Typography>
                                  <Typography variant="body2">{hit.motion_subtype}</Typography>
                                </>
                              )}
                              {hit.notice_given_date && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Notice Given:</Typography>
                                  <Typography variant="body2">{hit.notice_given_date}</Typography>
                                </>
                              )}
                              {hit.last_updated_date && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Last Updated:</Typography>
                                  <Typography variant="body2">{hit.last_updated_date}</Typography>
                                </>
                              )}
                              {hit.source_url && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Source URL:</Typography>
                                  <Link href={hit.source_url} target="_blank" rel="noopener" variant="body2">
                                    View on ParlWork
                                  </Link>
                                </>
                              )}
                            </Box>
                          )}

                          <Box sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              onClick={() => toggleMetadataExpansion(hit.id)}
                              sx={{ textTransform: 'none', fontSize: '0.875rem', p: 0, minWidth: 'auto' }}
                            >
                              {expandedMetadata[hit.id] ? 'Hide metadata' : 'Show all metadata'}
                            </Button>
                            {hit.source_url && (
                              <Link
                                href={hit.source_url}
                                target="_blank"
                                rel="noopener"
                                sx={{ fontWeight: 500, ml: 2 }}
                              >
                                View on ParlWork
                              </Link>
                            )}
                          </Box>
                        </>
                      }
                    />
                  </ListItem>
                  {index < results.hits.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            {results.total > perPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(results.total / perPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
